import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { loadAllProducts } from '@services/dataService';
import {
  buildIndex,
  isIndexReady,
  getTypeAheadSuggestions,
  search as fuseSearch,
} from '@services/searchService';
import { decodeItemCode, looksLikeItemCode, DecoderError } from '@services/decoderService';
import DecodedProductCard from '@components/product/DecodedProductCard';
import Breadcrumbs from '@components/layout/Breadcrumbs';

/**
 * ProductFinderPage — Wave 4 route /product-finder.
 *
 * Offline-first per [[feedback-offline-first-cache-strategy]]:
 *   1. Type-ahead runs against the local Fuse.js index (searchService).
 *      That index is built once on page mount from the same static
 *      products-*.json bundle every other MRT page uses.
 *   2. On enter (or exact match), if the code exists locally the
 *      product's PDP link is shown alongside the decoded card, which
 *      is populated from the local product record (no network).
 *   3. Only if the code is NOT known locally does the page fall
 *      back to the public admin-api decode endpoint, so an unknown
 *      code from a fresh SAP extract still gets a decoded card.
 *
 * The decoded card mirrors PDP layout by reusing PDPAccordionSection.
 */
export default function ProductFinderPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const [query, setQuery] = useState(initialQuery);
  const [indexBuilt, setIndexBuilt] = useState(isIndexReady());
  const [suggestions, setSuggestions] = useState([]);
  const [plpHits, setPlpHits] = useState([]);
  const [decoded, setDecoded] = useState(null);
  const [localMatch, setLocalMatch] = useState(null);
  const [decodeError, setDecodeError] = useState(null);
  const [decodeLoading, setDecodeLoading] = useState(false);

  const allProductsRef = useRef([]);
  const abortRef = useRef(null);

  // Build the index once on mount. buildIndex is a no-op if already built.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const products = await loadAllProducts();
        if (cancelled) return;
        allProductsRef.current = products;
        if (!isIndexReady()) buildIndex(products);
        setIndexBuilt(true);
      } catch (err) {
        console.error('[product-finder] failed to build search index', err);
        // Even without index, the decoder endpoint still works — page
        // remains partially usable for unknown-code lookups.
        setIndexBuilt(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Type-ahead + live PLP hits every time the query changes.
  useEffect(() => {
    if (!indexBuilt) { setSuggestions([]); setPlpHits([]); return; }
    if (!query || query.trim().length < 2) {
      setSuggestions([]); setPlpHits([]);
      return;
    }
    // Code-first type-ahead (Vandana 2026-08-05):
    //   1. exact itemCode match wins outright.
    //   2. otherwise, itemCode PREFIX match — case-insensitive, surface
    //      only codes that START with what the user typed. This is the
    //      primary UX: typing "7538" expects "7538-*" codes, NOT fuzzy
    //      substring matches like "DP0758*" from Fuse.
    //   3. If the input looks like a code (starts with a digit or a
    //      known letter-prefix such as BG / DP / RH), we DELIBERATELY
    //      do NOT fall through to fuzzy — showing description-fuzzy
    //      hits for a code prefix is confusing (the user is clearly
    //      hunting for a code, not descriptive text).
    //   4. Otherwise (input looks like free text words), fall back to
    //      Fuse's fuzzy multi-field getTypeAheadSuggestions.
    // The PLP "matching families" strip below the search bar still
    // uses fuzzy fuseSearch so free-text discoverability stays intact
    // there.
    const trimmed = query.trim();
    const upper = trimmed.toUpperCase();
    const looksLikeCode = /^(\d|BG|DP|RH)/i.test(trimmed);
    const exact = allProductsRef.current.find(
      (p) => (p.itemCode || '').toUpperCase() === upper,
    );
    if (exact) {
      setSuggestions([exact]);
    } else {
      const prefixHits = allProductsRef.current
        .filter((p) => (p.itemCode || '').toUpperCase().startsWith(upper))
        .slice(0, 5);
      if (prefixHits.length > 0 || looksLikeCode) {
        // Code-shaped input: show prefix matches only (may be empty).
        setSuggestions(prefixHits);
      } else {
        // Free-text words: fuzzy fallback across all fields.
        setSuggestions(getTypeAheadSuggestions(trimmed, 5));
      }
    }
    setPlpHits(fuseSearch(trimmed, { limit: 10 }));
  }, [query, indexBuilt]);

  // Handler for the search submit (Enter or Search button).
  const runSearch = async (raw) => {
    setDecodeError(null);
    const value = (raw ?? query).trim();
    if (!value) {
      setDecoded(null); setLocalMatch(null); setDecodeError(null);
      return;
    }
    setSearchParams(value ? { q: value } : {}, { replace: true });

    // 1. Offline-first — local exact match wins. Product data shows
    //    immediately from the local catalog; if the code shape suggests
    //    it's decoder-covered, we ALSO fire the public decoder call in
    //    the background so the "Decode steps" section can render the
    //    engine's segment trace. Failure is silent — the local answer
    //    is the primary payload.
    const local = allProductsRef.current.find(
      (p) => (p.itemCode || '').toUpperCase() === value.toUpperCase(),
    );
    if (local) {
      setLocalMatch(local);
      setDecoded(buildLocalDecoded(local));
      if (looksLikeItemCode(value)) {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        decodeItemCode(value, { signal: abortRef.current.signal })
          .then((payload) => {
            if (!payload) return;
            setDecoded((prev) => (prev ? {
              ...prev,
              segments: payload.segments || [],
              matched_rule: payload.matched_rule || prev.matched_rule,
              regional_non_standard: !!payload.regional_non_standard,
            } : prev));
          })
          .catch((err) => {
            if (err.name === 'AbortError') return;
            // Local answer already visible — swallow enrichment errors.
          });
      }
      return;
    }
    setLocalMatch(null);

    // 2. Shape guard — free-text won't decode cleanly, don't waste a call.
    if (!looksLikeItemCode(value)) {
      setDecoded(null);
      setDecodeError({
        code: 'FREE_TEXT',
        message:
          'That looks like free-text search — press Enter with a product code, or pick a suggestion.',
      });
      return;
    }

    // 3. Miss → fall back to the public decoder endpoint.
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setDecodeLoading(true);
    try {
      const payload = await decodeItemCode(value, { signal: abortRef.current.signal });
      setDecoded(payload);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setDecoded(null);
      setDecodeError(
        err instanceof DecoderError
          ? { code: err.code, message: err.message }
          : { code: 'UNKNOWN', message: err.message || 'Decode failed.' },
      );
    } finally {
      setDecodeLoading(false);
    }
  };

  // Kick off on initial render if q= was in the URL.
  useEffect(() => {
    if (initialQuery && indexBuilt) runSearch(initialQuery);
    // Intentionally omit runSearch from deps — we only want the initial fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indexBuilt]);

  const showingLocal = Boolean(localMatch);
  const decodedCard = useMemo(() => (decoded ? <DecodedProductCard decoded={decoded} /> : null), [decoded]);

  return (
    <div className="min-h-[60vh] bg-white">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Breadcrumbs />
        <h1 className="text-2xl font-normal text-carbon-black font-sandvik-headline mt-2 mb-1">
          Product finder
        </h1>
        <p className="text-sm text-n6 mb-5">
          Enter a Sandvik Rock Tools item code to look up its decoded
          specifications. Works offline for known catalog codes.
        </p>

        <form
          data-testid="product-finder-form"
          className="flex gap-2 items-start"
          onSubmit={(e) => { e.preventDefault(); runSearch(); }}
        >
          <div className="flex-1 relative">
            <input
              data-testid="product-finder-input"
              type="text"
              inputMode="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. 42-6KGE172-S69H or 7538-6302-F70"
              className="w-full border border-n3 rounded-none px-3 py-2 text-base text-carbon-black focus:outline-none focus:border-electric-blue"
              aria-label="Item code"
            />
            {suggestions.length > 0 && (
              <ul
                data-testid="product-finder-suggestions"
                className="absolute left-0 right-0 top-full z-10 border border-n2 bg-white shadow-sm"
              >
                {suggestions.map((s) => (
                  <li key={s.itemCode || s.sku}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-n1"
                      onClick={() => {
                        setQuery(s.itemCode || '');
                        runSearch(s.itemCode || '');
                      }}
                    >
                      <span className="font-mono text-carbon-black">{s.itemCode}</span>
                      <span className="ml-2 text-n5">{s.description || s.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="submit"
            className="rounded-none border border-carbon-black bg-carbon-black text-white px-5 py-2 hover:bg-n8"
          >
            Search
          </button>
        </form>

        {decodeLoading && (
          <p data-testid="product-finder-loading" className="mt-4 text-sm text-n6">
            Looking up decoder…
          </p>
        )}

        {decodeError && (
          <div
            data-testid="product-finder-error"
            role="alert"
            className="mt-4 border border-vibrant-orange bg-sandvik-orange-light px-3 py-2 text-sm text-sandvik-orange-dark"
          >
            {decodeError.message}
          </div>
        )}

        {decodedCard && (
          <div className="mt-6 space-y-2">
            {showingLocal && (
              <p data-testid="product-finder-source-local" className="text-xs text-n5">
                Product data sourced from the local catalog. Decode steps
                enriched from the admin-api decoder when online.
              </p>
            )}
            {!showingLocal && decoded && (
              <p data-testid="product-finder-source-remote" className="text-xs text-n5">
                Sourced from the admin-api decoder (item not in local catalog).
              </p>
            )}
            {decodedCard}
            {showingLocal && localMatch?.itemCode && (
              <Link
                data-testid="product-finder-pdp-link"
                to={`/products/detail/${encodeURIComponent(localMatch.itemCode)}`}
                className="inline-block mt-2 text-sm text-electric-blue underline"
              >
                Open the full product detail page →
              </Link>
            )}
          </div>
        )}

        {plpHits.length > 0 && (
          <section className="mt-8">
            <h2 className="text-lg font-normal text-carbon-black font-sandvik-headline mb-2">
              Matching products in the catalog
            </h2>
            <ul data-testid="product-finder-plp-hits" className="divide-y divide-n2 border border-n2">
              {plpHits.map((p) => (
                <li key={p.itemCode || p.sku} className="px-3 py-2 text-sm flex justify-between">
                  <Link
                    to={`/products/detail/${encodeURIComponent(p.itemCode)}`}
                    className="text-electric-blue hover:underline font-mono"
                  >
                    {p.itemCode}
                  </Link>
                  <span className="text-n6 text-right ml-4">{p.description || p.name}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

/**
 * Build a decoder-shaped payload from a local product record. Wave 5
 * (admin PWA enrichment) will eventually bake decoder output into the
 * emitted product JSON so this becomes a straight passthrough; today
 * it maps the fields that already exist on the static bundle.
 */
function buildLocalDecoded(product) {
  const attrs = {};
  const skip = new Set([
    'itemCode', 'sku', 'name', 'description', 'imageUrl', 'imageUrl2',
    '_domain', '_categoryId', 'images', 'category', 'categoryName',
    'searchKeywords', 'competitorRef',
  ]);
  Object.entries(product).forEach(([k, v]) => {
    if (skip.has(k)) return;
    if (v === null || v === undefined) return;
    if (typeof v === 'object' && !Array.isArray(v)) return;
    attrs[k] = v;
  });
  return {
    item_code: product.itemCode,
    product_line: product._domain || product.productLine || null,
    family: product.category || product.categoryName || null,
    confidence: 'high',
    matched_rule: 'local-catalog',
    regional_non_standard: false,
    is_new: false,
    segments: [],
    attributes: attrs,
    warnings: [],
    cross_check: {
      found_in_tbl_products: true,
      matches_stored: true,
      warnings: [],
      schema_drift: [],
    },
  };
}
